import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomDropdown from "../../../../custom/customDropdown";
import { AuthContext } from "../../../../Header/auth - context";
import { uzmiGresku } from "../../../../custom/greske";
import "./VozilaOprema.css";

/* Oprema vozila (MB #145) — šta prikolica treba da ima: saobraćajna, čip za
   gorivo, CEMT bela potvrda, metla, šipke, španeri, palete…

   Vozač pri pokretanju ture sa promenjenom prikolicom na telefonu štiklira ovaj
   spisak (ista prikolica — samo potvrda). Donja tabela pokazuje ko je kad šta
   preuzeo i šta je falilo, pa se zna ko odgovara.

   Šablon firme: spisak jednog vozila se sačuva kao šablon i jednim klikom
   primeni na drugo vozilo (dodaju se samo stavke koje mu fale). */

const prazno = { naziv: "", kolicina: 1 };

const VozilaOprema = () => {
    const { t } = useTranslation();
    const toast = useRef(null);
    const auth = useContext(AuthContext);
    const vehicle = useSelector((state) => state.vehicle);
    const company = useSelector((state) => state.company);

    const [vozilaDB, setVozilaDB] = useState([]);
    const [idVozila, setIdVozila] = useState(null);
    const [stavke, setStavke] = useState([]);
    const [sablon, setSablon] = useState([]);
    const [primopredaje, setPrimopredaje] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [forma, setForma] = useState(prazno);
    const [izabranaId, setIzabranaId] = useState(null);
    const [cuva, setCuva] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const zovi = useCallback(async (telo) => {
        const res = await fetch(process.env.REACT_APP_BACKEND_URL + "/vehicles/oprema", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify(telo),
        });
        if (!res.ok) throw res;
        return res.json();
    }, [auth.token]);

    const greska = async (e, rezerva) => {
        const g = await uzmiGresku(e, rezerva);
        toast.current?.show({ severity: "error", summary: t("common.greska"), detail: g.message, life: 5000 });
    };
    const uspeh = (poruka) => toast.current?.show({ severity: "success", summary: t("common.uspeh"), detail: poruka, life: 3000 });

    /* ── spisak vozila ── */
    useEffect(() => {
        let ziv = true;
        (async () => {
            try {
                const d = await zovi({ step: "INIT" });
                if (!ziv) return;
                setVozilaDB(d.VozilaDB || []);
                setSablon(d.SablonDB || []);
            } catch (e) { if (ziv) greska(e, t("oprema.greskaUcitavanja")); }
        })();
        return () => { ziv = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Vozilo izabrano u Pregledu se preuzima jednom (isti obrazac kao Dokumenta). */
    useEffect(() => {
        if (!vehicle || !vozilaDB.length || idVozila != null) return;
        if (vozilaDB.some((v) => String(v.value) === String(vehicle))) setIdVozila(vehicle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle, vozilaDB]);

    const ucitaj = useCallback(async (id) => {
        if (id == null) { setStavke([]); setPrimopredaje([]); return; }
        setUcitava(true);
        try {
            const d = await zovi({ step: "GET", id_vozilo: id });
            setStavke(d.StavkeDB || []);
            setSablon(d.SablonDB || []);
            setPrimopredaje(d.PrimopredajeDB || []);
        } catch (e) {
            greska(e, t("oprema.greskaUcitavanja"));
            setStavke([]); setPrimopredaje([]);
        } finally {
            setUcitava(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zovi]);

    useEffect(() => {
        setIzabranaId(null);
        setForma(prazno);
        ucitaj(idVozila);
    }, [idVozila, ucitaj]);

    /* Toggle izbora reda — bez onRowClick na tabeli (vidi CLAUDE.md). */
    const izaberi = (red) => {
        setIzabranaId((pre) => {
            const sledeci = String(pre) === String(red.id) ? null : red.id;
            setForma(sledeci != null ? { naziv: red.naziv, kolicina: red.kolicina } : prazno);
            return sledeci;
        });
    };

    const sacuvaj = async () => {
        if (!forma.naziv.trim()) {
            toast.current?.show({ severity: "warn", summary: t("common.greska"), detail: t("oprema.nazivObavezan"), life: 3000 });
            return;
        }
        setCuva(true);
        try {
            const d = await zovi({
                step: "SAVE_STAVKA", id: izabranaId, id_vozilo: idVozila,
                naziv: forma.naziv.trim(), kolicina: forma.kolicina || 1,
            });
            uspeh(d.message);
            setIzabranaId(null);
            setForma(prazno);
            ucitaj(idVozila);
        } catch (e) {
            greska(e, t("oprema.greskaCuvanja"));
        } finally {
            setCuva(false);
        }
    };

    const obrisi = () => confirmDialog({
        message: t("oprema.obrisiPitanje", { naziv: stavke.find((s) => s.id === izabranaId)?.naziv || "" }),
        header: t("oprema.obrisiNaslov"),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: t("common.da"),
        rejectLabel: t("common.ne"),
        accept: async () => {
            try {
                const d = await zovi({ step: "DELETE_STAVKA", id: izabranaId });
                uspeh(d.message);
                setIzabranaId(null);
                setForma(prazno);
                ucitaj(idVozila);
            } catch (e) { greska(e, t("oprema.greskaCuvanja")); }
        },
    });

    const primeniSablon = async () => {
        try {
            const d = await zovi({ step: "PRIMENI_SABLON", id_vozilo: idVozila });
            uspeh(d.message);
            ucitaj(idVozila);
        } catch (e) { greska(e, t("oprema.greskaCuvanja")); }
    };

    const sacuvajSablon = () => confirmDialog({
        message: t("oprema.sablonPitanje", { n: stavke.length }),
        header: t("oprema.sacuvajSablon"),
        icon: "pi pi-copy",
        acceptLabel: t("common.da"),
        rejectLabel: t("common.ne"),
        accept: async () => {
            try {
                const d = await zovi({ step: "SACUVAJ_SABLON", id_vozilo: idVozila });
                uspeh(d.message);
                ucitaj(idVozila);
            } catch (e) { greska(e, t("oprema.greskaCuvanja")); }
        },
    });

    const registracija = vozilaDB.find((v) => String(v.value) === String(idVozila))?.label || "";

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Oprema");
        const headerRow = worksheet.addRow([t("oprema.kolNaziv"), t("oprema.kolKolicina")]);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        stavke.forEach((r) => {
            const row = worksheet.addRow([r.naziv, r.kolicina]);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
        }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `${t("oprema.naslov")} ${registracija} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const zaglavlje = (
        <div className="export-buttons fakture-stavke-header">
            <div style={{ display: "flex", gap: "0.4rem" }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel}
                    data-pr-tooltip="XLS" disabled={!stavke.length} />
                {izabranaId != null && (
                    <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={obrisi}
                        data-pr-tooltip={t("oprema.obrisiNaslov")} />
                )}
                <Button type="button" icon="pi pi-download" severity="info" rounded onClick={primeniSablon}
                    disabled={!sablon.length} data-pr-tooltip={t("oprema.primeniSablon", { n: sablon.length })} />
                <Button type="button" icon="pi pi-copy" severity="secondary" rounded onClick={sacuvajSablon}
                    disabled={!stavke.length} data-pr-tooltip={t("oprema.sacuvajSablon")} />
            </div>
            <input type="search" onInput={(e) => setGlobalFilter(e.target.value)}
                placeholder={t("common.search")} className="p-inputtext p-component" />
        </div>
    );

    const teloStanje = (r) => (
        <span className={"oprema-stanje " + (r.sveUredu ? "oprema-stanje-ok" : "oprema-stanje-fali")}>
            {r.sveUredu ? t("oprema.sveUredu") : t("oprema.fali")}
        </span>
    );

    return (
        <div className="zaposleni_table_container" id="vozila_oprema">
            <Toast ref={toast} />
            <ConfirmDialog />
            <Tooltip target=".export-buttons>button" position="bottom" />

            <div style={{ display: "flex", flexDirection: "column", width: "fit-content", maxWidth: "320px", marginBottom: "1%" }}>
                <label htmlFor="id_vozilo_oprema">{t("admin.dokIzaberiVozilo")}:</label>
                <CustomDropdown
                    id="id_vozilo_oprema"
                    name="id_vozilo_oprema"
                    value={idVozila}
                    options={vozilaDB}
                    optionLabel="label"
                    filter
                    onChange={(e) => setIdVozila(e.target.value ?? null)}
                    placeholder={t("admin.dokIzaberiVozilo")}
                    filterPlaceholder={t("gps.pretraziRegistraciju")}
                    emptyMessage={t("gps.nemaVozila")}
                    style={{ width: "100%", minWidth: "220px" }}
                />
            </div>

            {idVozila == null ? (
                <p className="infoPoruka" style={{ margin: "1em 0" }}>{t("oprema.izaberiVozilo")}</p>
            ) : (
                <div className="col-12">
                    <div className="blagajnaDashboard dnevniceDashboard">
                        <Card title={izabranaId != null ? t("oprema.izmenaStavke") : t("oprema.novaStavka")}>
                            <div className="oprema-forma">
                                <div className="oprema-polje oprema-polje-naziv">
                                    <label htmlFor="oprema_naziv">{t("oprema.kolNaziv")}</label>
                                    <InputText id="oprema_naziv" value={forma.naziv} maxLength={200}
                                        placeholder={t("oprema.nazivPh")}
                                        onChange={(e) => setForma({ ...forma, naziv: e.target.value })} />
                                </div>
                                <div className="oprema-polje">
                                    <label htmlFor="oprema_kolicina">{t("oprema.kolKolicina")}</label>
                                    <InputNumber inputId="oprema_kolicina" value={forma.kolicina} min={1} max={9999}
                                        showButtons onValueChange={(e) => setForma({ ...forma, kolicina: e.value || 1 })} />
                                </div>
                                <button type="submit" onClick={sacuvaj} disabled={cuva}
                                    style={{ minWidth: "20%", marginTop: "2%" }} className="btn btn-success">
                                    {izabranaId != null ? t("common.sacuvaj").toUpperCase() : t("admin.unos").toUpperCase()}
                                </button>
                            </div>
                            <p className="oprema-savet">{t("oprema.savet")}</p>
                        </Card>

                        <Card>
                            <DataTable
                                className="turePregledTable"
                                value={stavke}
                                loading={ucitava}
                                header={zaglavlje}
                                globalFilter={globalFilter}
                                paginator rows={10}
                                scrollable scrollHeight="50vh"
                                resizableColumns columnResizeMode="expand"
                                emptyMessage={t("oprema.nemaStavki")}
                                rowClassName={(r) => (String(r.id) === String(izabranaId) ? { "selected-row": true } : {})}
                            >
                                <Column header={t("nova.izaberi")} style={{ minWidth: "90px" }}
                                    body={(r) => (
                                        <Button label={t("nova.izaberi")} icon="pi pi-eye" type="button"
                                            className="p-button-text p-button-sm" onClick={() => izaberi(r)} />
                                    )} />
                                <Column field="naziv" header={t("oprema.kolNaziv")} sortable style={{ minWidth: "220px" }} />
                                <Column field="kolicina" header={t("oprema.kolKolicina")} sortable style={{ minWidth: "100px" }}
                                    bodyClassName="oprema-broj" />
                            </DataTable>
                        </Card>

                        <Card title={t("oprema.primopredajeNaslov")}>
                            <DataTable
                                className="turePregledTable"
                                value={primopredaje}
                                loading={ucitava}
                                paginator rows={10}
                                scrollable scrollHeight="40vh"
                                resizableColumns columnResizeMode="expand"
                                emptyMessage={t("oprema.nemaPrimopredaja")}
                            >
                                <Column field="Datum" header={t("oprema.kolDatum")} style={{ minWidth: "150px" }} />
                                <Column field="Vozac" header={t("oprema.kolVozac")} style={{ minWidth: "160px" }} />
                                <Column field="Tura" header={t("oprema.kolTura")} style={{ minWidth: "120px" }} />
                                <Column header={t("oprema.kolStanje")} body={teloStanje} style={{ minWidth: "120px" }} />
                                <Column header={t("oprema.kolPrikolica")} style={{ minWidth: "130px" }}
                                    body={(r) => (r.istaPrikolica ? t("oprema.ista") : t("oprema.preuzeta"))} />
                                <Column field="Nedostaje" header={t("oprema.kolNedostaje")} style={{ minWidth: "200px" }}
                                    body={(r) => r.Nedostaje || "—"} />
                                <Column field="napomena" header={t("oprema.kolNapomena")} style={{ minWidth: "200px" }}
                                    body={(r) => r.napomena || "—"} />
                            </DataTable>
                        </Card>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VozilaOprema;
